/**
 * Every word on the card the hub opens behind: the control legend, the call to play, and what a
 * failure to start says instead.
 *
 * Chrome. This is the last screen before the fiction and the only place a failure can be shown,
 * since console logging is banned by the lint rules, so it says what it means. See
 * [src/text/AGENTS.md](../../../text/AGENTS.md).
 */

import { actionKeyLabel, moveKeysLegend } from '@/ui/keyLabels';

export const HUB_CARD_TITLE = 'The hub';
export const HUB_CARD_LEAVE = 'Back to menu';

/** The one failure the card can show, above whatever went wrong underneath it. */
export const HUB_CARD_FAILED = 'Cannot open the hub';

/** Shown while the physics runtime loads. There is no room to build: the room is always there. */
export const HUB_CARD_LOADING = 'Opening the door';

export const HUB_CARD_READY_POINTER = 'Click to walk in';
export const HUB_CARD_READY_TOUCH = 'Tap to walk in';

/**
 * What the hub is for, in one line, on the screen somebody reads before they walk into a room
 * where nothing is trying to kill them and wonder what they are supposed to do.
 */
export const HUB_CARD_NOTE = 'Nothing here is hostile. Hold the key on a station to use it.';

/**
 * The key column reads the live bindings rather than spelling letters, on the terms
 * `rangeCardText.ts` states: fetched when the card is built, so a rebind is already on it.
 */
export function hubKeyControls(): readonly (readonly [string, string])[] {
  return [
    ['Mouse', 'look'],
    [moveKeysLegend(), 'move'],
    [actionKeyLabel('sprint'), 'sprint'],
    [actionKeyLabel('crouch'), 'crouch'],
    [actionKeyLabel('jump'), 'jump'],
    [actionKeyLabel('interact'), 'use a station'],
    [actionKeyLabel('cycleView'), 'first or third person'],
    [actionKeyLabel('swapShoulder'), 'swap shoulder'],
    ['Esc', 'release the mouse'],
  ];
}

export const HUB_TOUCH_CONTROLS: readonly (readonly [string, string])[] = [
  ['Drag', 'look'],
  ['Left stick', 'move'],
  ['Use', 'hold on a station'],
  ['Jump, Duck', 'jump, crouch'],
  ['View', 'first or third person'],
  ['Menu', 'back to this card'],
];

/**
 * The two chips on the menu card.
 *
 * Here rather than at the card, because a fact is copy: `copy-check.mjs` is what enforces it, and
 * the labels are the part most likely to want rewording once a station does something.
 */
export const HUB_FACT_STATIONS = 'Stations';

/**
 * The chip that says why this card has no Host co-op button on it. A player who can host the range
 * and not this is owed the reason on the card rather than left to wonder what they missed.
 */
export const HUB_FACT_PLAYERS = 'Players';
export const HUB_FACT_PLAYERS_VALUE = '1';
